import { clientLogger } from '@/lib/clientLogger';
'use client';

import { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { UserOrganizationDTO } from '@/types';
import { useAuth } from './useAuth';
import { getStorageItem, setStorageItem, removeStorageItem, SELECTED_ORG_STORAGE_KEY } from '@/lib/capacitor';
import { isNative } from '@/lib/capacitor';
import { cleanupOnLogout, initializeAndStartTracking } from '@/lib/locationService';

const STORAGE_KEY = SELECTED_ORG_STORAGE_KEY;

interface GlobalOrganizationContextType {
    organizations: UserOrganizationDTO[];
    isLoading: boolean;
    /** True when the organizations request completed successfully (including an empty list). */
    organizationsLoadedSuccessfully: boolean;
    selectedOrgId: string;
    selectedOrg: UserOrganizationDTO | null;
    orgReady: boolean;
    setSelectedOrgId: (id: string) => void;
    switchOrganization: (id: string) => void;
    clearOrganization: () => void;
    refetchOrganizations: () => void;
}

const GlobalOrganizationContext = createContext<GlobalOrganizationContextType | undefined>(undefined);

interface GlobalOrganizationProviderProps {
    children: ReactNode;
}

export function GlobalOrganizationProvider({ children }: GlobalOrganizationProviderProps) {
    const [selectedOrgId, setSelectedOrgIdState] = useState<string>('');
    const [storageChecked, setStorageChecked] = useState(false);
    const hasAutoSelected = useRef(false);
    const queryClient = useQueryClient();

    const { isAuthenticated } = useAuth();

    const {
        data: organizations = [],
        isLoading,
        isFetching,
        isSuccess: organizationsLoadedSuccessfully,
        refetch: refetchOrganizations,
    } = useQuery({
        queryKey: ['my-organizations'],
        queryFn: () => apiService.getMyOrganizations(),
        enabled: isAuthenticated,
    });

    // Check storage for previously selected org on mount
    useEffect(() => {
        if (!isAuthenticated) {
            setSelectedOrgIdState('');
            setStorageChecked(false);
            hasAutoSelected.current = false;
            return;
        }

        if (!isAuthenticated) return;
        let cancelled = false;
        getStorageItem(STORAGE_KEY).then((storedOrgId) => {
            if (!cancelled && storedOrgId) {
                setSelectedOrgIdState(storedOrgId);
            }
            if (!cancelled) {
                setStorageChecked(true);
            }
        });
        return () => { cancelled = true; };
    }, [isAuthenticated]);

    // Auto-select logic: runs once after orgs are loaded and storage is checked.
    //
    // isFetching, not just isLoading: this list is persisted to IndexedDB for a week, so on a cold
    // start react-query rehydrates the PREVIOUS session's memberships and reports isLoading=false
    // while it revalidates. Latching on that picks an organization the user may no longer be in —
    // and because the latch is permanent, the correction never runs: the stored selection stays
    // invalid, orgReady goes false, and they are bounced back to the picker after choosing.
    // Waiting for the fetch to settle costs nothing offline, where it settles to an error and
    // leaves the persisted list in place, which is exactly what offline mode wants.
    useEffect(() => {
        if (hasAutoSelected.current || isLoading || isFetching || !storageChecked || organizations.length === 0) return;

        // If stored org is in the list, keep it selected (already set from storage)
        if (selectedOrgId && organizations.some(o => o.organization_id === selectedOrgId)) {
            hasAutoSelected.current = true;
            apiService.setOrgId(selectedOrgId);
            return;
        }

        // If exactly 1 org, auto-select it
        if (organizations.length === 1) {
            const orgId = organizations[0].organization_id;
            setSelectedOrgIdState(orgId);
            apiService.setOrgId(orgId);
            setStorageItem(STORAGE_KEY, orgId);
            hasAutoSelected.current = true;
            return;
        }

        // Multiple orgs, no valid stored selection — user must pick
        // Clear invalid stored value if any
        if (selectedOrgId) {
            setSelectedOrgIdState('');
            removeStorageItem(STORAGE_KEY);
        }
        hasAutoSelected.current = true;
    }, [organizations, isLoading, isFetching, storageChecked, selectedOrgId]);

    const setSelectedOrgId = useCallback((id: string) => {
        setSelectedOrgIdState(id);
        apiService.setOrgId(id);
        setStorageItem(STORAGE_KEY, id);
    }, []);

    const switchOrganization = useCallback((id: string) => {
        setSelectedOrgIdState(id);
        apiService.setOrgId(id);
        setStorageItem(STORAGE_KEY, id);
        // Invalidate all queries so data reloads for the new org
        queryClient.invalidateQueries();
    }, [queryClient]);

    const clearOrganization = useCallback(() => {
        setSelectedOrgIdState('');
        apiService.setOrgId(null);
        removeStorageItem(STORAGE_KEY);
        hasAutoSelected.current = false;
    }, []);

    // orgReady: true when an org is selected AND it's valid
    const orgReady = useMemo(() => {
        if (!storageChecked || isLoading) return false;
        if (!selectedOrgId) return false;
        return organizations.some(o => o.organization_id === selectedOrgId);
    }, [storageChecked, isLoading, selectedOrgId, organizations]);

    const selectedOrg = useMemo(() => {
        if (!selectedOrgId) return null;
        return organizations.find(o => o.organization_id === selectedOrgId) || null;
    }, [organizations, selectedOrgId]);

    useEffect(() => {
        if (!isAuthenticated || !selectedOrg || !isNative()) {
            return;
        }

        const syncLocationTracking = async () => {
            if (selectedOrg.role?.toLowerCase() === 'technician') {
                await initializeAndStartTracking();
                return;
            }

            await cleanupOnLogout();
        };

        syncLocationTracking().catch((error) => {
            clientLogger.error('[GlobalOrganization] Failed to sync location tracking for selected organization:', error);
        });
    }, [isAuthenticated, selectedOrg]);

    const value = useMemo(() => ({
        organizations,
        isLoading,
        organizationsLoadedSuccessfully,
        selectedOrgId,
        selectedOrg,
        orgReady,
        setSelectedOrgId,
        switchOrganization,
        clearOrganization,
        refetchOrganizations: () => { void refetchOrganizations(); },
    }), [
        organizations,
        isLoading,
        organizationsLoadedSuccessfully,
        selectedOrgId,
        selectedOrg,
        orgReady,
        setSelectedOrgId,
        switchOrganization,
        clearOrganization,
        refetchOrganizations,
    ]);

    return (
        <GlobalOrganizationContext.Provider value={value}>
            {children}
        </GlobalOrganizationContext.Provider>
    );
}

export function useGlobalOrganization() {
    const context = useContext(GlobalOrganizationContext);
    if (context === undefined) {
        throw new Error('useGlobalOrganization must be used within a GlobalOrganizationProvider');
    }
    return context;
}
